/**
 * Blog index
 *
 * Every article on the blog is in the page already, so filtering by category
 * and stepping through pages is a matter of hiding cards rather than fetching
 * anything. The chosen category and page are remembered for the session, so
 * coming back from an article lands the reader where they left off.
 */
if (!customElements.get("blog-index")) {
  const STORAGE_KEY = "blogIndexState";
  const ALL = "ALL";

  // Past this many pages the row switches from every number to a window
  // around the current one, so it never outgrows the grid it sits under.
  const MAX_VISIBLE_PAGES = 7;

  // Where the stylesheet brings the rail in and the grid changes shape, so the
  // two page sizes the section is configured with change hands here too.
  const DESKTOP = "(min-width: 1024px)";

  const readState = () => {
    try {
      return JSON.parse(window.sessionStorage.getItem(STORAGE_KEY)) || {};
    } catch (_error) {
      return {};
    }
  };

  const writeState = state => {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (_error) {
      // Storage can be full or switched off; the page works without it.
    }
  };

  class BlogIndex extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.grid = this.querySelector("[data-grid]");
      this.cards = Array.from(this.querySelectorAll(".blog-index-card"));

      if (!this.grid || this.cards.length === 0) return;

      this.filters = Array.from(this.querySelectorAll("[data-filter]"));
      this.emptyMessage = this.querySelector("[data-empty]");
      this.pagination = this.querySelector("[data-pagination]");
      this.pageList = this.querySelector("[data-pages]");
      this.perPageDesktop = Math.max(parseInt(this.dataset.perPageDesktop, 10) || 12, 1);
      this.perPageMobile = Math.max(parseInt(this.dataset.perPageMobile, 10) || 6, 1);
      this.pageLabel = this.dataset.pageLabel || "";

      this.desktop = window.matchMedia(DESKTOP);
      this.desktop.addEventListener("change", () => this.render({ scroll: false }));

      this.filters.forEach(filter => {
        filter.addEventListener("click", () => this.setCategory(filter.dataset.filter));
      });

      if (this.pageList) {
        this.pageList.addEventListener("click", event => {
          const button = event.target.closest("[data-page]");
          if (button) {
            this.setPage(Number(button.dataset.page), { scroll: true });
          }
        });
      }

      const state = readState();
      const known = this.filters.some(filter => filter.dataset.filter === state.category);

      this.category = known ? state.category : ALL;
      this.page = Number.isInteger(state.page) ? state.page : 0;

      // The restored view is where the page starts, so it must not scroll.
      this.render({ scroll: false });
    }

    get perPage() {
      return this.desktop.matches ? this.perPageDesktop : this.perPageMobile;
    }

    setCategory(category) {
      if (category === this.category) return;

      this.category = category;
      this.page = 0;
      this.render({ scroll: false });
    }

    setPage(page, options) {
      if (page === this.page) return;

      this.page = page;
      this.render(options);
    }

    render({ scroll } = {}) {
      const perPage = this.perPage;

      // Crossing the breakpoint changes how many cards a page holds. Carry the
      // reader across on the article they were looking at rather than on a
      // page number that now points somewhere else.
      if (this.appliedPerPage && this.appliedPerPage !== perPage) {
        this.page = Math.floor((this.page * this.appliedPerPage) / perPage);
      }
      this.appliedPerPage = perPage;

      const visible = this.cards.filter(
        card => this.category === ALL || card.dataset.category === this.category
      );
      const pageCount = Math.ceil(visible.length / perPage);

      this.page = Math.min(Math.max(this.page, 0), Math.max(pageCount - 1, 0));

      const first = this.page * perPage;
      const last = first + perPage;
      const onPage = new Set(visible.slice(first, last));

      this.cards.forEach(card => {
        card.hidden = !onPage.has(card);
      });

      this.filters.forEach(filter => {
        filter.setAttribute("aria-pressed", filter.dataset.filter === this.category ? "true" : "false");
      });

      if (this.emptyMessage) {
        this.emptyMessage.hidden = visible.length > 0;
      }

      this.renderPagination(pageCount);
      writeState({ category: this.category, page: this.page });

      if (scroll) {
        this.scrollToGrid();
      }
    }

    renderPagination(pageCount) {
      if (!this.pagination || !this.pageList) return;

      // A single page of results has nothing to navigate between.
      this.pagination.hidden = pageCount < 2;

      if (pageCount < 2) {
        this.pageList.replaceChildren();
        return;
      }

      const items = BlogIndex.pageSequence(this.page, pageCount).map(page => {
        const item = document.createElement("li");

        if (page === null) {
          item.className = "blog-index__page-gap";
          item.setAttribute("aria-hidden", "true");
          item.textContent = "…";
          return item;
        }

        const button = document.createElement("button");
        button.type = "button";
        button.className = "blog-index__page";
        button.dataset.page = String(page);
        button.textContent = String(page + 1);
        button.setAttribute("aria-label", this.pageLabel.replace("[page]", String(page + 1)));

        if (page === this.page) {
          button.setAttribute("aria-current", "page");
        }

        item.appendChild(button);
        return item;
      });

      this.pageList.replaceChildren(...items);
    }

    /**
     * The page numbers to show, with `null` standing in for a gap: every page
     * while they fit, otherwise the first, the last and the current page with
     * its neighbours.
     */
    static pageSequence(current, pageCount) {
      if (pageCount <= MAX_VISIBLE_PAGES) {
        return Array.from({ length: pageCount }, (_value, index) => index);
      }

      const shown = new Set([0, pageCount - 1, current]);

      // Keep the window a constant width, so the row does not shuffle sideways
      // as the reader steps through the middle of a long list.
      for (let offset = 1; offset < pageCount && shown.size < MAX_VISIBLE_PAGES - 2; offset += 1) {
        [current - offset, current + offset].forEach(page => {
          if (page > 0 && page < pageCount - 1 && shown.size < MAX_VISIBLE_PAGES - 2) {
            shown.add(page);
          }
        });
      }

      const pages = Array.from(shown).sort((a, b) => a - b);

      return pages.flatMap((page, index) => {
        const previous = pages[index - 1];
        return index > 0 && page - previous > 1 ? [null, page] : [page];
      });
    }

    scrollToGrid() {
      const header =
        parseInt(
          getComputedStyle(document.documentElement).getPropertyValue("--header-height"),
          10
        ) || 0;
      const top = this.grid.getBoundingClientRect().top + window.scrollY - header - 24;

      window.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
    }
  }

  customElements.define("blog-index", BlogIndex);
}
